import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { getSharedEvents, copyEvent } from "../../../../services/eventServices";
import { getAdherentsByUser } from "../../../../services/adherentServices";
import { useNotification } from "../../../../components/Notification/NotificationManager";

import {
  DEFAULT_SHARED_EVENT_FILTERS,
  countActiveFilters,
  dedupeSharedEventsById,
  filterSharedEvents,
  getSharedEventsStats,
  sortSharedEvents,
} from "../utils/sharedEventsUtils";

const normalizeAdherentsResponse = (response) => {
  if (Array.isArray(response)) return response;
  if (Array.isArray(response?.adherents)) return response.adherents;
  if (Array.isArray(response?.Adherents)) return response.Adherents;
  if (Array.isArray(response?.data)) return response.data;
  if (Array.isArray(response?.data?.adherents)) return response.data.adherents;
  return [];
};

const getCopyKey = (eventId, adherentId) => `${eventId}-${adherentId}`;

export function useSharedEventsPage() {
  const { addNotification } = useNotification();

  const notificationRef = useRef(addNotification);
  const hasLoadedOnceRef = useRef(false);
  const copyingKeysRef = useRef([]);

  const [events, setEvents] = useState([]);
  const [adherents, setAdherents] = useState([]);
  const [selectedAdherentId, setSelectedAdherentId] = useState(null);

  const [filters, setFilters] = useState(DEFAULT_SHARED_EVENT_FILTERS);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [isMapOpen, setIsMapOpen] = useState(false);

  const [loading, setLoading] = useState(true);
  const [adherentsLoading, setAdherentsLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const [copyingKeys, setCopyingKeys] = useState([]);
  const [copiedKeys, setCopiedKeys] = useState([]);

  const selectedAdherent = useMemo(() => {
    if (!adherents.length) return null;

    return (
      adherents.find(
        (adherent) => Number(adherent.id) === Number(selectedAdherentId),
      ) || adherents[0]
    );
  }, [adherents, selectedAdherentId]);

  useEffect(() => {
    notificationRef.current = addNotification;
  }, [addNotification]);

  useEffect(() => {
    copyingKeysRef.current = copyingKeys;
  }, [copyingKeys]);

  const fetchAdherents = useCallback(async () => {
    setAdherentsLoading(true);

    try {
      const response = await getAdherentsByUser();
      const loadedAdherents = normalizeAdherentsResponse(response);

      setAdherents(loadedAdherents);

      if (loadedAdherents.length > 0) {
        setSelectedAdherentId((currentId) => {
          const stillExists = loadedAdherents.some(
            (adherent) => Number(adherent.id) === Number(currentId),
          );

          return stillExists ? currentId : loadedAdherents[0].id;
        });
      } else {
        setSelectedAdherentId(null);

        notificationRef.current?.(
          "Aucun profil judoka n'est rattaché à votre compte.",
          "info",
        );
      }
    } catch (error) {
      console.error("Erreur lors de la récupération des adhérents :", error);

      notificationRef.current?.(
        "Erreur lors de la récupération de vos profils judokas.",
        "error",
      );
    } finally {
      setAdherentsLoading(false);
    }
  }, []);

  const fetchEvents = useCallback(async ({ silent = false } = {}) => {
    const isInitialLoad = !hasLoadedOnceRef.current;

    if (isInitialLoad) {
      setLoading(true);
    } else {
      setRefreshing(true);
    }

    try {
      const response = await getSharedEvents();
      const safeEvents = Array.isArray(response) ? response : [];

      const uniqueEvents = dedupeSharedEventsById(safeEvents);

      setEvents(sortSharedEvents(uniqueEvents));

      if (!silent && !isInitialLoad) {
        notificationRef.current?.("Événements partagés actualisés.", "success");
      }
    } catch (error) {
      console.error("Erreur lors de la récupération des événements :", error);

      notificationRef.current?.(
        "Erreur lors de la récupération des événements partagés.",
        "error",
      );
    } finally {
      hasLoadedOnceRef.current = true;
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchEvents({ silent: true });
    fetchAdherents();
  }, [fetchEvents, fetchAdherents]);

  const filteredEvents = useMemo(() => {
    return filterSharedEvents(events, filters);
  }, [events, filters]);

  const stats = useMemo(() => {
    return getSharedEventsStats(events);
  }, [events]);

  const activeFiltersCount = useMemo(() => {
    return countActiveFilters(filters);
  }, [filters]);

  const handleFilterChange = useCallback((name, value) => {
    setFilters((prev) => ({
      ...prev,
      [name]: value,
    }));
  }, []);

  const resetFilters = useCallback(() => {
    setFilters(DEFAULT_SHARED_EVENT_FILTERS);
  }, []);

  const handleOpenDetails = useCallback((event) => {
    setSelectedEvent(event);
    setIsDetailsOpen(true);
  }, []);

  const handleCloseDetails = useCallback(() => {
    setIsDetailsOpen(false);
    setSelectedEvent(null);
  }, []);

  const handleDepartmentSelect = useCallback((departmentCode) => {
    setFilters((prev) => ({
      ...prev,
      departement: departmentCode,
    }));

    setIsMapOpen(false);
  }, []);

  const selectAdherent = useCallback((adherentId) => {
    setSelectedAdherentId(adherentId);
  }, []);

  const isEventCopyingForSelectedAdherent = useCallback(
    (eventId) => {
      if (!eventId || !selectedAdherentId) return false;

      return copyingKeys.includes(getCopyKey(eventId, selectedAdherentId));
    },
    [copyingKeys, selectedAdherentId],
  );

  const isEventCopiedForSelectedAdherent = useCallback(
    (eventId) => {
      if (!eventId || !selectedAdherentId) return false;

      return copiedKeys.includes(getCopyKey(eventId, selectedAdherentId));
    },
    [copiedKeys, selectedAdherentId],
  );

  const handleCopyEvent = useCallback(
    async (eventId) => {
      if (!eventId) return;

      if (!selectedAdherentId) {
        notificationRef.current?.(
          "Veuillez sélectionner un judoka avant de copier l'événement.",
          "error",
        );
        return;
      }

      const copyKey = getCopyKey(eventId, selectedAdherentId);

      if (copyingKeysRef.current.includes(copyKey)) {
        return;
      }

      setCopyingKeys((prev) => [...prev, copyKey]);

      try {
        await copyEvent(eventId, selectedAdherentId);

        setCopiedKeys((prev) =>
          prev.includes(copyKey) ? prev : [...prev, copyKey],
        );

        const adherentName = [
          selectedAdherent?.prenom,
          selectedAdherent?.nom,
        ]
          .filter(Boolean)
          .join(" ");

        notificationRef.current?.(
          adherentName
            ? `Événement copié dans l'agenda de ${adherentName}.`
            : "Événement copié avec succès dans l'agenda du judoka.",
          "success",
        );
      } catch (error) {
        console.error("Erreur lors de la copie de l'événement :", error);

        const message =
          error?.response?.data?.message ||
          error?.message ||
          "Erreur lors de la copie de l'événement.";

        if (error?.response?.status === 409) {
          setCopiedKeys((prev) =>
            prev.includes(copyKey) ? prev : [...prev, copyKey],
          );
        }

        notificationRef.current?.(message, "error");
      } finally {
        setCopyingKeys((prev) => prev.filter((key) => key !== copyKey));
      }
    },
    [selectedAdherentId, selectedAdherent],
  );

  return {
    events,
    adherents,
    selectedAdherent,
    selectedAdherentId,

    filteredEvents,
    filters,
    selectedEvent,
    isDetailsOpen,
    isMapOpen,

    loading,
    adherentsLoading,
    refreshing,

    copyingKeys,
    copiedKeys,
    activeFiltersCount,
    stats,

    fetchEvents,
    fetchAdherents,

    setSelectedEvent,
    setIsDetailsOpen,
    setIsMapOpen,

    selectAdherent,
    handleFilterChange,
    resetFilters,
    handleCopyEvent,
    handleOpenDetails,
    handleCloseDetails,
    handleDepartmentSelect,

    isEventCopyingForSelectedAdherent,
    isEventCopiedForSelectedAdherent,
  };
}